/**
 * Dropzone file uploads (Dropzone.js) bound to a Stimulus controller.
 *
 * Markup:
 * - Outer element: `data-controller="dropzone"` plus value attributes below.
 * - Inner drop target: `data-dropzone-target="area"` (Dropzone attaches here).
 *
 * Required values:
 * - `data-dropzone-url-value` — POST endpoint for uploads.
 *
 * Optional values:
 * - `data-dropzone-frame-id-value` / `data-dropzone-frame-src-value` — after a
 *   successful upload, refreshes that `<turbo-frame>` (src reload / Turbo API).
 * - `data-dropzone-param-name-value` (default "media"), `data-dropzone-max-files-value`,
 *   `data-dropzone-accepted-files-value`, `data-dropzone-timeout-value`,
 *   `data-dropzone-add-remove-links-value`, `data-dropzone-parallel-uploads-value`
 *   — passed through to Dropzone. Extra request headers use CSRF from the page meta.
 *
 * Toast outlet (optional): `data-dropzone-toast-outlet="#toast"` so max-file,
 * success, and error messages use the global toast container from the layout.
 */
import { Controller } from "@hotwired/stimulus"
import Dropzone from "dropzone"
import { FILE_TYPES } from "../constants/upload_constants"
import { getMetaValue } from "../lib/meta_tags"
import { reloadTurboFrame } from "../lib/reload_turbo_frame"

Dropzone.autoDiscover = false

export default class extends Controller {
  static targets = ["area"]
  static values = {
    url: String,
    frameId: String,
    frameSrc: String,
    paramName: { type: String, default: "media" },
    acceptedFiles: { type: String, default: FILE_TYPES.ALL_MEDIA },
    timeout: { type: Number, default: 120000 },
    addRemoveLinks: { type: Boolean, default: true },
    parallelUploads: { type: Number, default: 1 },
    headers: Object
  }
  static outlets = ["toast"];

  connect() {
    this.dropzone = new Dropzone(this.areaTarget, {
      url: this.urlValue,
      method: "post",
      paramName: this.paramNameValue,
      acceptedFiles: this.acceptedFilesValue,
      timeout: this.timeoutValue,
      addRemoveLinks: this.addRemoveLinksValue,
      parallelUploads: 1,
      headers: this.headers,
      previewsContainer: false
    })

    this.bindEvents()
  }

  disconnect() {
    this.dropzone?.destroy()
  }

  get headers() {
    return { "X-CSRF-Token": getMetaValue("csrf-token") || "" };
  }

  bindEvents() {
    this.dropzone.on("maxfilesexceeded", (file) => this.#onMaxFilesExceeded(file))
    this.dropzone.on("success", (file) => this.#onUploadSuccess(file))
    this.dropzone.on("error", (file, message) => this.#onUploadError(file, message))
    this.dropzone.on("uploadprogress", (file, progress, bytesSent) => this.#onUploadProgress(file, progress, bytesSent))
  }

  #onMaxFilesExceeded(file) {
    this.dropzone.removeFile(file)
    this.toastOutlet?.show(
      `Only ${this.maxFilesValue} file${this.maxFilesValue === 1 ? "" : "s"} can be uploaded at once.`,
      "danger"
    )
  }

  #onUploadSuccess(file) {
    this.reloadFrame()
    if (file) this.toastOutlet?.show(`Uploaded ${file.name}`, "notice")
  }

  #onUploadError(file, message) {
    let errorMessage = message

    if (typeof message === "object" && message !== null) {
      errorMessage = Array.isArray(message.errors)
          ? message.errors.join(", ")
          : JSON.stringify(message)
    }

    this.toastOutlet?.show(
        `${errorMessage} - ${file?.type}`,
        "danger"
    )
  }

  #onUploadProgress(file, progress, bytesSent) {
    if (progress) this.toastOutlet?.show(`Uploading... ${Math.trunc(progress)}%`, "notice", true)
  }

  reloadFrame() {
    if (!this.hasFrameIdValue) return

    reloadTurboFrame({
      frameId: this.frameIdValue,
      frameSrc: this.hasFrameSrcValue ? this.frameSrcValue : null
    })
  }
}